---
title: "Natural-language scheduling constraints"
description: "Add plain-language scheduling preferences in Chosen — like 'mornings only' or 'avoid Fridays' — and the AI scheduler applies them when finding interview times."
category: "scheduling"
keywords:
  - "natural language scheduling"
  - "scheduling constraints"
  - "interview time preferences"
  - "scheduling preferences ATS"
schema: "HowTo"
nav:
  section: "guides"
  order: 50
  parent: "/guides/scheduling"
---

When you create a scheduling request, you can add preferences in plain language — "avoid Friday afternoons," "mornings only," "nothing before 10am." The AI scheduler reads them and factors them into which slots it offers. No rule builder, no dropdowns.

## Where to add them

The preferences field sits in the scheduling request setup, alongside the interviewers and meeting length. Type a sentence or two. The scheduler parses it when the request starts and applies it on top of everyone's [availability windows](/guides/scheduling/availability-windows) and live calendars.

## Phrasing that works

Keep it concrete and time-shaped. The scheduler handles:

- **Times of day** — "mornings only," "afternoons preferred," "nothing before 10am."
- **Days** — "avoid Fridays," "Tuesday or Wednesday ideally."
- **Combinations** — "avoid Friday afternoons," "early in the week, mornings."
- **Soft leanings** — "prefer late afternoon" nudges without hard-blocking.

Vague phrasing gives vague results. "Sometime good for everyone" tells the scheduler nothing it didn't already know — it's always looking for a time good for everyone.

## They're best-effort

These constraints are preferences, not guarantees. The scheduler weighs them against real availability. If "mornings only" lines up with open calendar time, you'll see morning slots. If mornings are jammed for the next five business days, a soft preference may yield to a free afternoon so the interview can still happen.

<Callout type="info" title="Soft preferences bend; hard constraints don't">
A leaning like "prefer mornings" can give way when mornings are full. A hard limit like "only mornings" is treated as a wall — the scheduler won't cross it, even if that means finding nothing.
</Callout>

## When a constraint rules out everything

If your hard constraints eliminate every possible slot — "only Monday mornings" against a fully booked Monday — the scheduler does not quietly pick something else and hope. It stops, and tells you in the [AI events tab](/guides/scheduling/ai-events-tab) that no slot fits.

That's the intended behavior. A scheduler that guessed past your constraints would be worse than one that admits the constraints can't be met. When it stops, loosen the wording or widen an availability window and run the request again.

## When it gets it wrong

- **It missed a preference.** Long or buried phrasing is easy to misread. Keep the constraint to a short, standalone sentence near the front.
- **It treated a soft preference as hard, or the reverse.** Wording decides this. "Prefer" and "ideally" read as soft; "only" and "must" read as hard. Pick the verb that matches what you mean.
- **The slots ignore the constraint entirely.** Usually that means real availability left no choice — check the interviewers' [availability windows](/guides/scheduling/availability-windows) and calendars before blaming the parser.

## Limits

- Constraints apply to one scheduling request. They are not a saved org-wide rule — set those as [availability windows](/guides/scheduling/availability-windows) instead.
- They guide *which* slots get offered. They don't change the roughly five-business-day search horizon.
